import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Clock, MapPin, Award, Users, ArrowLeft, X, Sparkles, ShoppingCart, CalendarClock, Check, ShieldCheck, PlayCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/context/AuthContext";
import ReviewsSection from "@/components/ReviewsSection";

function fmtMxn(n) {
  if (n === null || n === undefined) return null;
  return "$" + Number(n).toLocaleString("es-MX") + " MXN";
}
function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function ProgramaDetalle() {
  const { slug } = useParams();
  const [prog, setProg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const { cart, addItem } = useCart();
  const { user } = useAuth();
  const { hasAllAccess } = useSubscription();
  const isAlumnoRole = !!user?.roles?.includes("alumno");
  const inCart = !!(prog && cart?.items?.some((i) => i.program_id === prog.id));
  const canBuy = !!(prog && prog.price_mxn && prog.next_cohort_start);
  const iadaEligible = !!(prog && prog.iada_aval_available);
  const [avalModal, setAvalModal] = useState(false);

  const handleAdd = () => {
    if (!canBuy || inCart) return;
    if (iadaEligible) {
      setAvalModal(true);
    } else {
      addItem(prog.id, false);
    }
  };

  const confirmAdd = (withAval) => {
    addItem(prog.id, withAval);
    setAvalModal(false);
  };

  useEffect(() => {
    api.get(`/catalog/programs/${slug}`)
      .then((r) => setProg(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads/public", { ...form, source: "program_detail", program_slug: slug });
      toast.success("Solicitud enviada. Te contactaremos pronto.");
      setModal(false);
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-3xl font-bold text-[#0B0B0B]">Programa no encontrado</h1>
        <Link to="/catalogo" className="mt-6 inline-block text-[#F26A21] hover:text-[#0B0B0B]">← Volver al catálogo</Link>
      </div>
    </PublicLayout>
  );

  if (!prog) return <PublicLayout><div className="max-w-3xl mx-auto px-4 py-24 text-gray-600">Cargando...</div></PublicLayout>;

  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/catalogo" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#0B0B0B] mb-6" data-testid="back-to-catalog">
          <ArrowLeft className="w-4 h-4" /> Volver al catálogo
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">
              {prog.area?.name} · <span className="font-mono">{prog.code}</span>
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B0B0B] font-display leading-tight" data-testid="program-title">
              {prog.title}
            </h1>

            <div className="mt-6 flex flex-wrap gap-3">
              <Chip icon={<Award className="w-3.5 h-3.5" />} label={prog.format} />
              <Chip icon={<Clock className="w-3.5 h-3.5" />} label={prog.duration_text} />
              <Chip icon={<MapPin className="w-3.5 h-3.5" />} label={prog.modality} />
            </div>

            {iadaEligible && (
              <div className="mt-6 p-4 rounded-lg bg-[#F26A21] text-[#0B0B0B] flex items-start gap-3" data-testid="program-iada-banner">
                <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm font-semibold">
                  Programa con aval académico de IADA — Instituto de Alta Dirección · Anáhuac Business School (en alianza con DCC).
                  El aval es opcional y agrega +30% al precio base.
                </p>
              </div>
            )}

            {prog.upcoming_cohorts && prog.upcoming_cohorts.length > 0 && (
              <section className="mt-10 space-y-3" data-testid="program-upcoming-cohorts">
                <h2 className="text-xs uppercase tracking-widest text-gray-600 font-semibold">Próximas cohortes</h2>
                <ul className="space-y-2">
                  {prog.upcoming_cohorts.map((c) => (
                    <li key={c.id} className="p-3 bg-[#F9FAFB] border border-gray-200 rounded-lg flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-[#0B0B0B]">{c.name}</p>
                        <p className="text-xs text-gray-600 font-mono">{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</p>
                      </div>
                      <span className="text-xs text-gray-600 font-mono">
                        {c.enrolled_count || 0}/{c.capacity || 0} cupo
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="mt-10 space-y-3">
              <h2 className="text-xs uppercase tracking-widest text-gray-600 font-semibold">Descripción</h2>
              <p className="text-[#0B0B0B] leading-relaxed" data-testid="program-description">{prog.description}</p>
            </section>

            <section className="mt-10 space-y-3">
              <h2 className="text-xs uppercase tracking-widest text-gray-600 font-semibold">Temario resumen</h2>
              <pre className="whitespace-pre-wrap font-sans text-[#0B0B0B] leading-relaxed p-4 bg-[#F9FAFB] border border-gray-200 rounded-lg">
                {prog.syllabus_summary}
              </pre>
            </section>

            <section className="mt-10 space-y-3">
              <h2 className="text-xs uppercase tracking-widest text-gray-600 font-semibold">Audiencia objetivo</h2>
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-[#F26A21] flex-shrink-0 mt-0.5" />
                <p className="text-[#0B0B0B]">{prog.target_audience}</p>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 p-6 bg-[#F9FAFB] border border-gray-200 rounded-xl space-y-4">
              {hasAllAccess && (
                <div
                  className="p-3 rounded-lg bg-primary/10 border border-primary/40 flex items-start gap-2"
                  data-testid="program-all-access-badge"
                >
                  <ShieldCheck className="w-4 h-4 text-[#B84A0C] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#B84A0C]">
                      Incluido en tu All-Access
                    </p>
                    <p className="text-[11px] text-[#B84A0C]/80 mt-0.5">
                      Puedes iniciarlo sin cargo adicional.
                    </p>
                  </div>
                </div>
              )}
              {prog.price_mxn && (
                <div data-testid="program-price-block">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Inversión</p>
                  <p className="mt-1 text-3xl font-bold text-[#0B0B0B] font-mono">{fmtMxn(prog.price_mxn)}</p>
                  <p className="text-xs text-gray-500 mt-0.5">+ IVA</p>
                  {iadaEligible && (
                    <p className="mt-2 text-[11px] text-[#F26A21] font-semibold" data-testid="program-iada-price-note">
                      + Aval IADA disponible (+30% · {fmtMxn(Math.round(prog.price_mxn * 1.3))})
                    </p>
                  )}
                  {prog.next_cohort_start && (
                    <p className="mt-2 inline-flex items-center gap-1 text-xs text-[#F26A21] font-semibold">
                      <CalendarClock className="w-3 h-3" aria-hidden="true" />
                      Próxima cohorte: {fmtDate(prog.next_cohort_start)}
                    </p>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={handleAdd}
                disabled={!canBuy || inCart}
                data-testid="add-to-cart-btn"
                aria-label={inCart ? "Ya en carrito" : canBuy ? "Agregar al carrito" : "Sin cohortes disponibles"}
                title={inCart ? "Ya está en tu carrito" : canBuy ? "Agregar al carrito" : "Sin cohortes públicas disponibles"}
                className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-md font-semibold transition-colors ${
                  inCart
                    ? "bg-emerald-500/20 text-emerald-800 border border-emerald-500/40 cursor-default"
                    : canBuy
                      ? "bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B]"
                      : "bg-[#F26A21]/30 text-[#0B0B0B]/50 cursor-not-allowed"
                }`}
              >
                {inCart ? (<><Check className="w-4 h-4" /> En carrito</>) : (<><ShoppingCart className="w-4 h-4" /> Agregar al carrito</>)}
              </button>
              {hasAllAccess && isAlumnoRole && (
                <Link
                  to={`/app/alumno/programas/${prog.id}`}
                  data-testid="start-with-all-access-btn"
                  className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold transition-colors"
                >
                  <PlayCircle className="w-4 h-4" /> Iniciar con All-Access
                </Link>
              )}
              {!hasAllAccess && prog.price_mxn && (
                <Link
                  to="/planes"
                  data-testid="program-upsell-all-access"
                  className="block text-center text-xs text-[#B84A0C] hover:text-primary font-semibold underline underline-offset-2"
                >
                  o incluido con All-Access desde $2,500/mes →
                </Link>
              )}
              {!canBuy && (
                <p className="text-[11px] text-gray-500 text-center">Sin cohortes públicas — solicita información.</p>
              )}
              {inCart && (
                <Link to="/carrito" data-testid="go-to-cart-link" className="block w-full text-center text-xs text-[#F26A21] hover:text-[#D9541A] font-semibold">
                  Ir al carrito →
                </Link>
              )}
              <div className="pt-2 border-t border-gray-200" />
              <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Solicita información</p>
              <p className="text-sm text-[#0B0B0B]">
                Recibirás detalles de inversión, fechas, temario completo y opciones corporativas.
              </p>
              <button
                onClick={() => setModal(true)}
                data-testid="request-info-btn"
                className="w-full py-3 rounded-md border border-[#F26A21] text-[#F26A21] hover:bg-[#F26A21]/10 font-semibold transition-all"
              >
                Solicitar información
              </button>
              <div className="pt-4 border-t border-gray-200 space-y-2 text-sm text-gray-600">
                <p className="flex justify-between"><span>Código</span><span className="font-mono text-[#0B0B0B]">{prog.code}</span></p>
                <p className="flex justify-between"><span>Formato</span><span className="text-[#0B0B0B]">{prog.format}</span></p>
                <p className="flex justify-between"><span>Duración</span><span className="text-[#0B0B0B]">{prog.duration_text}</span></p>
                <p className="flex justify-between"><span>Modalidad</span><span className="text-[#0B0B0B]">{prog.modality}</span></p>
              </div>
            </div>
          </aside>
        </div>

        {/* Fase 11 · Reviews section */}
        <ReviewsSection slug={prog.slug} programId={prog.id} />
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setModal(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            data-testid="lead-modal-form"
            className="w-full max-w-lg bg-[#F9FAFB] border border-gray-200 rounded-xl p-6 space-y-4"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#F26A21]">Solicitar información</p>
                <h3 className="text-xl font-semibold text-[#0B0B0B] mt-1">{prog.title}</h3>
              </div>
              <button type="button" onClick={() => setModal(false)} className="text-gray-600 hover:text-[#0B0B0B]" aria-label="cerrar">
                <X />
              </button>
            </div>
            <Input label="Nombre completo" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="lead-name" />
            <Input label="Correo corporativo" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="lead-email" />
            <Input label="Teléfono" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="lead-phone" />
            <Input label="Empresa" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="lead-company" />
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">Mensaje</label>
              <textarea
                data-testid="lead-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
                className="w-full mt-1 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-submit-btn"
              className="w-full py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold disabled:opacity-60"
            >
              {submitting ? "Enviando..." : "Enviar solicitud"}
            </button>
          </form>
        </div>
      )}
      {avalModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
             onClick={() => setAvalModal(false)} data-testid="iada-choice-modal">
          <div className="w-full max-w-md bg-white rounded-xl border border-gray-200 p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#F26A21] font-semibold">Aval académico</p>
                <h3 className="mt-1 text-lg font-bold text-[#0B0B0B]">¿Quieres agregar aval IADA?</h3>
              </div>
              <button onClick={() => setAvalModal(false)} data-testid="iada-modal-close"
                      aria-label="Cerrar" className="text-gray-400 hover:text-[#0B0B0B]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              <span className="font-semibold text-[#0B0B0B]">{prog.title}</span> es elegible para aval
              académico de IADA — Instituto de Alta Dirección · Anáhuac Business School.
            </p>
            <div className="space-y-2">
              <button type="button" onClick={() => confirmAdd(false)}
                data-testid="iada-modal-choose-no"
                className="w-full text-left p-3 rounded-lg border border-gray-300 hover:border-[#F26A21] transition-colors">
                <p className="text-sm font-semibold text-[#0B0B0B]">Sin aval</p>
                <p className="text-xs text-gray-500 mt-0.5">Certificado DCC estándar</p>
                <p className="mt-2 font-mono text-base font-bold text-[#0B0B0B]">{fmtMxn(prog.price_mxn)}</p>
              </button>
              <button type="button" onClick={() => confirmAdd(true)}
                data-testid="iada-modal-choose-yes"
                className="w-full text-left p-3 rounded-lg border-2 border-[#F26A21] bg-[#F26A21]/5 hover:bg-[#F26A21]/10 transition-colors">
                <p className="text-sm font-semibold text-[#0B0B0B] flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-[#F26A21]" />
                  Con aval IADA <span className="text-[10px] text-[#F26A21] font-mono">+30%</span>
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Certificado avalado por Universidad Anáhuac</p>
                <p className="mt-2 font-mono text-base font-bold text-[#F26A21]">
                  {fmtMxn(Math.round(prog.price_mxn * 1.3))}
                </p>
              </button>
            </div>
            <p className="mt-4 text-[10px] text-gray-500 text-center">
              Puedes cambiar tu elección desde el carrito antes de pagar.
            </p>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}

function Chip({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-[#0B0B0B] bg-black/5 border border-gray-300 rounded">
      {icon}{label}
    </span>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}{required && <span className="text-red-400 ml-1">*</span>}</label>
      <input
        data-testid={testid}
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]"
      />
    </div>
  );
}
